"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";

const Guide = () => {
  const steps = [
    {
      title: "Tell us about yourself",
      description: "Stream - Marks - Budget - Country",
    },
    {
      title: "Get your Fit Score",
      description: "Real admissions data",
    },
    {
      title: "Apply with guidance",
      description: "SOP - Documents - Visa Prep",
    },
    {
      title: "Track Live",
      description: "Real time dashboard. No chasing",
    },
  ];

  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Reset then re-trigger so animation plays fresh every time
          setIsVisible(false);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setIsVisible(true);
            });
          });
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="mx-auto bg-[linear-gradient(98.9deg,_#8F3EBC_0.58%,_#0F1217_51.94%,_#1776F0_100.41%)]">

      <div className="container max-w-[1400px]  py-8 sm:py-10 md:py-14 lg:py-16 px-4 xxs:px-6 sm:px-[40px]">
        <div className="relative overflow-hidden backdrop-blur-md bg-white/[0.06] border border-white/15 rounded-3xl p-6 sm:p-10 md:p-12 lg:p-16 flex flex-col lg:flex-row items-stretch justify-between gap-10 lg:gap-16">

          {/* Decorative Concentric Rings in Bottom Right */}
          <div className="absolute -bottom-16 -right-16 w-[200px] h-[200px] border border-white/[0.08] rounded-full pointer-events-none"></div>
          <div className="absolute -bottom-28 -right-28 w-[300px] h-[300px] border border-white/[0.06] rounded-full pointer-events-none"></div>
          <div className="absolute -bottom-40 -right-40 w-[400px] h-[400px] border border-white/[0.04] rounded-full pointer-events-none"></div>

          {/* Left Column */}
          <div className="flex flex-col justify-center items-center lg:items-start text-center lg:text-start lg:flex-1 max-w-[600px] gap-6 lg:gap-8 relative z-10">

            <div>
              <p className="text-[10px] xxs:text-[11px] sm:text-xs font-bold tracking-wider text-white/80 uppercase mb-2 xxs:mb-3 sm:mb-6 ff_poppins">
                EDVIA: YOUR AI STUDY MENTOR
              </p>
              <h2 className="text-white font-semibold text-[20px] xxs:text-[22px] xs:text-[26px] sm:text-3xl md:text-4xl lg:text-[48px] leading-[1.2] lg:leading-[48px] mb-0 lg:mb-6 ff_raleway">
                Guided by AI. <br />
                <span className="text-white/60">
                  Backed by real mentors.
                </span>
              </h2>
            </div>

            <Link
              href={"/tools/course-finder"}
              className="text-white text-center w-full lg:w-auto text-sm sm:text-base md:text-lg ff_poppins py-3 sm:py-4 px-8 font-semibold rounded-2xl bg-primary-gradient hover:opacity-90 transition-all duration-300 ease-in-out hover:scale-[1.02] focus:scale-[1.02] shadow-[0px_4px_12px_rgba(23,118,240,0.3)]"
            >
              Check Eligibility Free
            </Link>
          </div>

          {/* Right Column */}
          <div ref={sectionRef} className="flex-1 flex flex-col justify-center max-w-[500px] relative z-10">
            <div className="flex flex-col gap-6 sm:gap-8 relative">
              {steps.map((item, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-4 sm:gap-6 relative guide-step${isVisible ? " visible" : ""}`}
                  style={isVisible ? { animationDelay: `${index * 0.9}s` } : {}}
                >
                  {/* Connecting Line and Center Arrow */}
                  {index < steps.length - 1 && (
                    <div
                      className={`absolute left-[15px] sm:left-[21px] top-[37px] sm:top-[49px] w-[1px] bg-white guide-line${isVisible ? " visible" : ""}`}
                      style={isVisible ? { animationDelay: `${index * 0.9 + 0.5}s` } : {}}
                    >
                      <span className="absolute bottom-[-5px] left-1/2 -translate-x-1/2 text-white text-[8px] sm:text-[10px] select-none">
                        ▼
                      </span>
                    </div>
                  )}

                  {/* Step Bubble */}
                  <div className="z-10 flex items-center justify-center w-8 h-8 sm:w-11 sm:h-11 rounded-full bg-[#1776F0] text-white font-bold text-xs sm:text-sm shrink-0 shadow-lg">
                    {index + 1}
                  </div>

                  {/* Step Info */}
                  <div className="pt-0.5 sm:pt-1">
                    <h3 className="text-white font-semibold text-sm xxs:text-[15px] xs:text-base sm:text-lg ff_poppins leading-snug">
                      {item.title}
                    </h3>
                    <p className="text-white/60 text-[11px] xxs:text-xs xs:text-sm sm:text-[18px] font-normal leading-[1.2] sm:leading-[18px] ff_poppins mt-0.5 sm:mt-1">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>

  );
};

export default Guide;

